/**
 * Le poche chiavi che non possono sparire.
 *
 * ## Il rischio, detto senza giri
 *
 * La coda delle timbrature offline vive nel `localStorage` (`attendanceQueue.scriviCoda`). In una
 * WebView il `localStorage` è memoria che il sistema operativo considera **cache**: Android la
 * cancella quando lo spazio scarseggia, iOS quando l'app resta inutilizzata a lungo, e nessuno dei
 * due avvisa. Una timbratura fatta in un capannone senza campo, e cancellata prima del ritorno
 * della rete, è un'ora di lavoro che non esiste più — e non c'è modo di accorgersene, perché la
 * coda vuota è indistinguibile dalla coda svuotata con successo.
 *
 * L'archiviazione **nativa** scrive dove il sistema **non** ripulisce: `SharedPreferences` su
 * Android, `UserDefaults` su iOS. È lo stesso posto in cui un'app nativa terrebbe le sue cose.
 *
 * ## Due gusci, e perché il ripiego non è pigrizia
 *
 * ⚠️ Quel posto si raggiunge con un plugin: `cordova-plugin-nativestorage`. Non è un dettaglio di
 * portabilità — è **la** ragione per cui questo file va toccato quando si cambia guscio: senza,
 * l'APK Cordova tornerebbe ad avere la coda in `localStorage` nudo, cioè esattamente il rischio
 * descritto qui sopra, e **senza nessun errore**: `rispecchia` non troverebbe il plugin, tacerebbe,
 * e la copia su disco semplicemente non ci sarebbe.
 *
 * Il plugin è un globale sincrono da interrogare, così
 * lo stesso `dist/` funziona in tutti e due i pacchetti finché convivono.
 *
 * ## Perché uno specchio e non un trasloco
 *
 * `Preferences` è asincrona, la coda è sincrona, e riscrivere la coda in asincrono significherebbe
 * toccare ogni funzione che la usa — cioè riaprire la parte più delicata del percorso timbrature
 * per un problema che è di conservazione, non di logica.
 *
 * Qui il `localStorage` resta la fonte di verità del momento e ogni scrittura viene **rispecchiata**
 * su disco. All'avvio si fa il contrario: se il disco ha qualcosa e la memoria no, la memoria viene
 * riempita da lui. Il caso che conta — «il sistema ha ripulito la cache mentre l'app era chiusa» — è
 * esattamente questo.
 *
 * Il verso della copia non è simmetrico apposta: memoria vuota + disco pieno = ripristino; memoria
 * piena vince sempre sul disco, perché è quella che l'app ha appena scritto.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';
import { STORAGE_KEYS } from './constants';
import { leggiChiave, writeKey } from './storage';

/** L'interfaccia del plugin Cordova, che è a callback e vive su `window`. */
interface NativeStorageCordova {
  setItem(key: string, value: string, ok: () => void, ko: (e: unknown) => void): void;
  getItem(chiave: string, ok: (v: string) => void, ko: (e: unknown) => void): void;
  remove(chiave: string, ok: () => void, ko: (e: unknown) => void): void;
}

function cordovaStorage(): NativeStorageCordova | null {
  const g = globalThis as unknown as { NativeStorage?: NativeStorageCordova };
  return g.NativeStorage ?? null;
}

/** Scrive, o cancella se `valore` è `null`. Risolve comunque: il chiamante non deve aspettare. */
async function writeOnDisk(chiave: string, valore: string | null): Promise<void> {
  const ns = cordovaStorage();
  /* ⚠️ Senza il plugin non si scrive, e **non si ripiega**: il ripiego naturale sarebbe il
     `localStorage`, che e' esattamente la memoria da cui questo file esiste per scappare. Meglio
     non aver copiato che aver copiato in un posto che Android cancella quando gli serve spazio.
     Qui c'era il ramo del guscio precedente, che nell'APK non veniva mai raggiunto. */
  if (!ns) return;
  await new Promise<void>((resolve) => {
    const fine = () => resolve();
    if (valore === null) ns.remove(chiave, fine, fine);
    else ns.setItem(chiave, valore, fine, fine);
  });
}

/** Legge, o `null` se la chiave non c'è — che per il plugin Cordova è un **errore**, non un vuoto. */
function readFromDisk(chiave: string): Promise<string | null> {
  const ns = cordovaStorage();
  if (!ns) return Promise.resolve(null);
  return new Promise<string | null>((risolvi) => {
    ns.getItem(chiave, (v) => risolvi(v ?? null), () => risolvi(null));
  });
}

/**
 * Le chiavi da proteggere. Volutamente **poche**: rispecchiare tutto renderebbe la scrittura più
 * lenta e il disco pieno di roba che si può ricalcolare. Qui sta solo ciò che, perso, non torna.
 */
export const DURABLE_KEYS = [
  /* La coda delle timbrature offline. Il nome non è più ripetuto a mano qui: sta nel registro
     (`STORAGE_KEYS`), che è anche il posto dove è scritto **perché** questa chiave è del
     dispositivo e non della persona — è la ragione per cui si può ripristinare al boot, quando
     nessuno è ancora entrato. */
  STORAGE_KEYS.attendanceQueue,
] as const;

/**
 * Copia su disco. Fire-and-forget di proposito: chi salva una timbratura non deve **aspettare** il
 * disco per vedersi confermare il gesto, e un fallimento qui non toglie nulla a ciò che è già in
 * memoria.
 */
export function mirrors(chiave: string, valore: string | null): void {
  /* ⚠️ `runtimeNativo()`, non `isNative()`. Quando questa riga e' stata scritta, `isNative()`
     interrogava il guscio precedente e nell'APK
     **vivo** — impacchettato con Cordova — rispondeva `false`. Il ramo Cordova di questo file
     (`cordovaStorage()`) c'era gia' ed era scritto: non veniva mai raggiunto, perche' la
     guardia usciva prima. La coda delle timbrature tornava cosi' al `localStorage` nudo, che e'
     **esattamente il rischio che questo file esiste per evitare** — in una WebView Android
     considera quella memoria cache, e la cancella quando ha bisogno di spazio. */
  if (nativeRuntime() === 'web') return;
  void writeOnDisk(chiave, valore).catch(() => undefined);
}

/**
 * Ripristina dal disco ciò che la memoria ha perso. Da attendere **prima** di montare l'app: la
 * coda viene letta al primo render, e idratare dopo significherebbe mostrare «nessuna timbratura in
 * attesa» a chi ne ha tre.
 *
 * Non solleva: se il plugin non risponde, l'app parte come è sempre partita.
 */
export async function hydrateFromDisk(keys: readonly string[] = DURABLE_KEYS): Promise<void> {
  if (nativeRuntime() === 'web') return;

  try {
    for (const chiave of keys) {
      // La memoria vince: se c'è già qualcosa, è più recente per costruzione.
      if (leggiChiave(chiave) !== null) continue;
      const valore = await readFromDisk(chiave);
      if (valore != null) writeKey(chiave, valore);
    }
  } catch { /* si parte comunque */ }
}
